/**
 * Service Worker Registration for LeadWix PWA
 *
 * Registers /service-worker.js, handles update lifecycle,
 * and notifies the app when a new version is available.
 */

const SW_URL = `${process.env.PUBLIC_URL}/service-worker.js`;

function isLocalhost() {
  return Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/)
  );
}

/**
 * register({ onSuccess, onUpdate })
 *  - onSuccess(registration): called when SW is installed for the first time
 *  - onUpdate(registration) : called when a new SW is waiting to activate
 */
export function register(config = {}) {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      if (isLocalhost()) {
        // Extra validation on localhost to avoid stale cache issues during dev
        checkValidServiceWorker(SW_URL, config);
        navigator.serviceWorker.ready.then(() => {
          console.log('[SW] Serving from local service worker (dev).');
        });
      } else {
        registerValidSW(SW_URL, config);
      }
    });

    // Detect controller change (new SW took over) and reload to apply updates
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });
  }
}

async function registerValidSW(swUrl, config) {
  try {
    const registration = await navigator.serviceWorker.register(swUrl);

    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      if (!installingWorker) return;

      installingWorker.onstatechange = () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New content is available — inform the app
            console.log('[SW] New content available. Will activate after reload.');
            if (config.onUpdate) config.onUpdate(registration);
          } else {
            // First install
            console.log('[SW] Content cached for offline use.');
            if (config.onSuccess) config.onSuccess(registration);
          }
        }
      };
    };
  } catch (error) {
    console.error('[SW] Registration failed:', error);
  }
}

async function checkValidServiceWorker(swUrl, config) {
  try {
    const response = await fetch(swUrl, { headers: { 'Service-Worker': 'script' } });
    const contentType = response.headers.get('content-type');

    if (response.status === 404 || (contentType && !contentType.includes('javascript'))) {
      // SW not found — unregister and reload
      const registration = await navigator.serviceWorker.ready;
      await registration.unregister();
      window.location.reload();
    } else {
      registerValidSW(swUrl, config);
    }
  } catch {
    console.log('[SW] No internet connection. App running in offline mode.');
  }
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error(error.message));
  }
}
